import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { CalculatedColumn } from '@bst/react-data-grid';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback, useDebounce } from 'use-debounce';

import { useStores } from '../../../stores/useStores';

import { CellFilter } from '../../CellFilter/Views/CellFilter';
import { FilterValueList } from './FilterValueList';
import { FilterType } from '../../../apiclients/api/types/FilterType';
import { columnFilterQueryKey } from '../Hooks/useColumnFilterValues';
import { FILTER_DEBOUNCE_TIMER } from '../Hooks/utils/constants';

/**
 * @property column - CalculatedColumn; Individual column from DataGrid columns
 * @property columnKey - string; Column key to map data key
 */
type Props<Row, SummaryRow> = {
  column: CalculatedColumn<Row, SummaryRow>;
  columnKey: string;
};

/**
 * @component CellDropdownFilter - This component is a part of User Management Grid.
 * @component Component renders Cell Filter button and Dropdown menu.
 * @component Component controls included, excluded and a search values to preserve state between Filter re-renders.
 * @component Component hosts FilterValueList to control Cell Filter's values search filter.
 */
export const CellDropdownFilter = observer(<Row, SummaryRow = unknown>({ columnKey }: Props<Row, SummaryRow>) => {
  const searchRef = useRef("")
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("");
  const [includedValues, setIncludedValues] = useState<Set<string>>(new Set())
  const [selectAllChecked, setSelectAllChecked] = useState(true);
  const { userManagementStore } = useStores();

  useEffect(() => {
    const currentFilter = userManagementStore.getFilterByKey(columnKey)
    if (!includedValues.size && currentFilter && currentFilter.filterType === FilterType.AutoComplete) {
      setIncludedValues(new Set(currentFilter.value))
      setSelectAllChecked(Boolean(currentFilter.exclude))
    }
    // if cell is out of view after horizontal scroll
    // element dismounts and looses state
    // apply existing filter values on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const debouncedApplySearch = useDebouncedCallback((value: string) => {
    if (value.length) {
      userManagementStore.applySearchFilter({
        key: columnKey,
        value: search,
        filterType: FilterType.Input
      })
    } else {
      userManagementStore.removeSearchFilter(columnKey)
    }
  }, FILTER_DEBOUNCE_TIMER)

  const [debouncedSearch] = useDebounce(Boolean(search.length), FILTER_DEBOUNCE_TIMER)

  const handleSearchFilter = useCallback((value: string) => {
    searchRef.current = value
    setSearch(value);
    debouncedApplySearch(value)
  }, [debouncedApplySearch])

  // Remove search on menu close
  // get values from previous query
  // apply search result data as autocomplete filter
  const handleOnMenuClose = useCallback(() => {
    if (searchRef.current) {
      const gridFilters = userManagementStore.getGridFilters()
      const columnFilters = userManagementStore.getColumnFilters(columnKey)
      const queryKey = [
        columnFilterQueryKey,
        {
          key: columnKey,
          filters: columnFilters
        }
      ];
      const prevData = queryClient.getQueryData<InfiniteData<string[], number>>(queryKey)
      if (prevData) {
        let newSet = new Set(prevData.pages.flatMap((page) => page))
        const currentFilter = gridFilters.filter((filter) => filter.key === columnKey).pop()
        if (currentFilter && currentFilter.filterType === FilterType.AutoComplete) {
          if (currentFilter.exclude) {
            currentFilter.value.forEach((v) => {
              newSet.delete(v)
            })
          } else {
            newSet = new Set(currentFilter.value)
          }
        }
        if (newSet.size) {
          setIncludedValues(newSet)
          setSelectAllChecked(!selectAllChecked)
          userManagementStore.applyFilter({
            key: columnKey,
            value: [...newSet],
            exclude: false,
            filterType: FilterType.AutoComplete
          })
        }
      }
    }
    handleSearchFilter("")
  }, [columnKey, handleSearchFilter, queryClient, selectAllChecked, userManagementStore])

  return (
    <CellFilter
      search={search}
      setSearch={handleSearchFilter}
      hasNotification={userManagementStore.hasActiveFilter(columnKey)}
    >
      <FilterValueList
        columnKey={columnKey}
        searchIsActive={debouncedSearch}
        includedValues={includedValues}
        selectAllChecked={selectAllChecked}
        setIncludedValues={setIncludedValues}
        setSelectAllChecked={setSelectAllChecked}
        onClose={handleOnMenuClose}
      />
    </CellFilter>
  );
})